/**
 * File System Access API Service
 * Implements local-first file operations using the File System Access API
 * No server dependencies - all operations are local to the user's device
 */

import JSZip from 'jszip';

const BINARY_EXTENSION_ALLOWLIST = new Set([
  '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.avif', '.ico',
  '.glb', '.gltf', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.zip', '.rar', '.7z', '.tar', '.gz', '.bz2', '.xz', '.bin', '.wasm'
]);

const BINARY_MIME_PREFIXES = ['image/', 'audio/', 'video/', 'model/'];

const BINARY_EXACT_MIME_TYPES = new Set([
  'application/octet-stream',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'application/x-zip-compressed',
  'application/x-7z-compressed',
  'application/vnd.rar',
  'application/x-tar',
  'application/gzip',
  'application/wasm'
]);

class FileSystemAccessService {
  constructor() {
    this.directoryHandle = null;
    this.packageDirectoryHandle = null; // Separate handle for package directory
    this.fileHandles = new Map(); // Cache file handles for performance
    this.packageFileHandles = new Map(); // Cache package file handles for performance
    this.isSupported = this.checkSupport();
    this.isFirefox = this.checkFirefox();
  }

  /**
   * Check if File System Access API is supported
   * @returns {boolean} - API support status
   */
  checkSupport() {
    if (typeof window === 'undefined') {
      return false;
    }

    return 'showDirectoryPicker' in window &&
           'showOpenFilePicker' in window &&
           'showSaveFilePicker' in window;
  }

  /**
   * Check if the browser is Firefox
   * @returns {boolean} - Firefox detection status
   */
  checkFirefox() {
    return typeof navigator !== 'undefined' && navigator.userAgent.includes('Firefox');
  }

  /**
   * Open inspection project folder
   * @returns {Promise<FileSystemDirectoryHandle>} - Directory handle
   */
  async openProjectFolder() {
    if (!this.isSupported) {
      if (this.isFirefox) {
        throw new Error('File System Access API is not supported in Firefox. Please use Chrome, Edge, or Opera for full functionality. You can still use the app in read-only mode.');
      } else {
        throw new Error('File System Access API is not supported in this browser. Please use a modern browser like Chrome, Edge, or Opera.');
      }
    }

    try {
      // Request directory handle from user
      this.directoryHandle = await window.showDirectoryPicker({
        mode: 'readwrite' // Request readwrite access for editing capabilities
      });

      // Clear cached file handles when opening new project
      this.fileHandles.clear();

      return this.directoryHandle;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Folder selection was cancelled by the user.');
      } else if (error.name === 'NotFoundError') {
        throw new Error('The selected folder was not found.');
      } else if (error.name === 'SecurityError') {
        throw new Error('Permission denied. Please ensure you have the necessary permissions to access this folder.');
      } else {
        throw new Error(`Failed to open project folder: ${error.message}`);
      }
    }
  }

  /**
   * Get directory handle (existing or request new one)
   * @returns {Promise<FileSystemDirectoryHandle>} - Directory handle
   */
  async getDirectoryHandle() {
    if (!this.directoryHandle) {
      return await this.openProjectFolder();
    }
    
    // Verify we still have permission to access the directory
    try {
      // Try to read the directory to verify permission
      // eslint-disable-next-line no-unused-vars
      for await (const _entry of this.directoryHandle.entries()) {
        break; // Just need to check if we can iterate
      }
      return this.directoryHandle;
    } catch (error) {
      if (error.name === 'SecurityError' || error.name === 'NotFoundError') {
        // Permission was revoked or directory was moved/deleted
        return await this.openProjectFolder();
      }
      throw error;
    }
  }

  /**
   * Index all files in the project directory
   * @returns {Promise<Array>} - Array of file items
   */
  async indexProjectFiles() {
    const directoryHandle = await this.getDirectoryHandle();
    const files = [];

    try {
      for await (const [name, handle] of directoryHandle.entries()) {
        if (handle.kind === 'file') {
          const file = await handle.getFile();
          files.push(this.normalizeFileItem(file, handle));
        } else if (handle.kind === 'directory') {
          // Recursively index subdirectories
          const subFiles = await this.indexDirectory(handle, name);
          files.push(...subFiles);
        }
      }
    } catch (error) {
      if (error.name === 'SecurityError') {
        throw new Error('Permission denied while accessing files. Please re-select the project folder.');
      }
      throw new Error(`Failed to index project files: ${error.message}`);
    }

    return files.sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Recursively index a directory
   * @param {FileSystemDirectoryHandle} dirHandle - Directory handle
   * @param {string} basePath - Base path for relative paths
   * @returns {Promise<Array>} - Array of file items
   */
  async indexDirectory(dirHandle, basePath = '') {
    const files = [];

    for await (const [name, handle] of dirHandle.entries()) {
      const relativePath = basePath ? `${basePath}/${name}` : name;

      if (handle.kind === 'file') {
        const file = await handle.getFile();
        files.push(this.normalizeFileItem(file, handle, relativePath));
      } else if (handle.kind === 'directory') {
        const subFiles = await this.indexDirectory(handle, relativePath);
        files.push(...subFiles);
      }
    }

    return files;
  }

  /**
   * Read file content using File System Access API
   * @param {Object} fileItem - File item from index
   * @returns {Promise<any>} - File content
   */
  async readFile(fileItem) {
    try {
      let fileHandle;

      // Try to get cached handle first
      if (this.fileHandles.has(fileItem.path)) {
        fileHandle = this.fileHandles.get(fileItem.path);
      } else {
        // Get fresh handle from directory
        const directoryHandle = await this.getDirectoryHandle();
        fileHandle = await this.getFileHandleFromPath(directoryHandle, fileItem.path);
        this.fileHandles.set(fileItem.path, fileHandle);
      }

      const file = await fileHandle.getFile();
      const content = await this.parseFileContent(file, fileItem);
      
      return content;
    } catch (error) {
      if (error.name === 'SecurityError') {
        throw new Error(`Permission denied reading ${fileItem.name}. Please re-select the project folder.`);
      } else if (error.name === 'NotFoundError') {
        throw new Error(`File ${fileItem.name} was not found. It may have been moved or deleted.`);
      }
      throw new Error(`Failed to read file ${fileItem.name}: ${error.message}`);
    }
  }

  /**
   * Save file content using File System Access API
   * @param {Object} fileItem - File item to save
   * @param {any} content - Content to save
   * @returns {Promise<boolean>} - Success status
   */
  async saveFile(fileItem, content) {
    try {
      let fileHandle;

      // Try to get cached handle first
      if (this.fileHandles.has(fileItem.path)) {
        fileHandle = this.fileHandles.get(fileItem.path);
      } else {
        // Get fresh handle from directory
        const directoryHandle = await this.getDirectoryHandle();
        fileHandle = await this.getFileHandleFromPath(directoryHandle, fileItem.path);
        this.fileHandles.set(fileItem.path, fileHandle);
      }

      // Create writable stream and save content
      const writable = await fileHandle.createWritable();
      
      if (typeof content === 'object') {
        // JSON content
        await writable.write(JSON.stringify(content, null, 2));
      } else if (Array.isArray(content)) {
        // TSV content
        const tsvContent = this.convertToTSV(content);
        await writable.write(tsvContent);
      } else {
        // Text content
        await writable.write(content.toString());
      }
      
      await writable.close();
      
      return true;
    } catch (error) {
      if (error.name === 'SecurityError') {
        throw new Error(`Permission denied saving ${fileItem.name}. Please re-select the project folder.`);
      } else if (error.name === 'NotFoundError') {
        throw new Error(`Cannot save ${fileItem.name}. The file or directory was not found.`);
      }
      throw new Error(`Failed to save file ${fileItem.name}: ${error.message}`);
    }
  }

  /**
   * Get file handle from relative path
   * @param {FileSystemDirectoryHandle} directoryHandle - Root directory handle
   * @param {string} relativePath - Relative path to file
   * @returns {Promise<FileSystemFileHandle>} - File handle
   */
  async getFileHandleFromPath(directoryHandle, relativePath) {
    const pathParts = relativePath.split('/');
    let currentHandle = directoryHandle;

    // Navigate to the parent directory
    for (let i = 0; i < pathParts.length - 1; i++) {
      currentHandle = await currentHandle.getDirectoryHandle(pathParts[i]);
    }

    // Get the file handle
    const fileName = pathParts[pathParts.length - 1];
    return await currentHandle.getFileHandle(fileName);
  }

  /**
   * Parse file content based on file type
   * @param {File} file - File object
   * @param {Object} fileItem - File item metadata
   * @returns {Promise<any>} - Parsed content
   */
  async parseFileContent(file, fileItem) {
    const extension = (fileItem.extension || '').toLowerCase();
    const mimeType = (file.type || fileItem.mimeType || '').toLowerCase();

    if (extension === '.json') {
      const text = await file.text();
      return JSON.parse(text);
    } else if (extension === '.txt' || extension === '.tsv' || extension === '.csv' || extension === '.md') {
      const text = await file.text();
      
      // Parse TSV/CSV files
      if (extension === '.tsv' || extension === '.csv' ||
          fileItem.name.includes('events') ||
          fileItem.name.includes('clusters') ||
          fileItem.name.includes('referencepoints') ||
          this.isTSVContent(text)) {
        return this.parseTSV(text);
      }
      
      return text;
    } else if (this.isBinaryFile(extension, mimeType)) {
      // Binary files are represented as object URLs to keep byte-exact reads.
      return URL.createObjectURL(file);
    }

    // Default to text
    return await file.text();
  }

  isBinaryFile(extension = '', mimeType = '') {
    if (mimeType) {
      if (BINARY_MIME_PREFIXES.some(prefix => mimeType.startsWith(prefix))) {
        return true;
      }

      if (BINARY_EXACT_MIME_TYPES.has(mimeType)) {
        return true;
      }
    }

    return BINARY_EXTENSION_ALLOWLIST.has(extension);
  }

  /**
   * Parse TSV/CSV content
   * @param {string} text - TSV/CSV text content
   * @returns {Array} - Parsed data
   */
  parseTSV(text) {
    const lines = text.trim().split('\n');
    if (lines.length === 0) return [];

    // Determine delimiter
    const firstLine = lines[0];
    const delimiter = firstLine.includes('\t') ? '\t' : ',';

    const headers = firstLine.split(delimiter).map(h => h.trim().replace(/^"|"$/g, '')); // Remove quotes if present
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(delimiter);
      const row = {};
      
      headers.forEach((header, index) => {
        let value = values[index] || '';
        // Remove quotes if present
        value = value.replace(/^"|"$/g, '');
        
        // Try to convert to number if possible
        const numValue = parseFloat(value);
        row[header] = isNaN(numValue) ? value : numValue;
      });
      
      data.push(row);
    }

    return data;
  }

  /**
   * Detect if text content contains TSV/CSV formatted data
   * @param {string} text - Text content to analyze
   * @returns {boolean} - Whether the text appears to be TSV/CSV formatted
   */
  isTSVContent(text) {
    // Skip empty content
    if (!text || text.trim().length === 0) {
      return false;
    }
    
    const lines = text.trim().split('\n');
    
    // Need at least 2 lines to be considered structured data
    if (lines.length < 2) {
      return false;
    }
    
    const firstLine = lines[0];
    const secondLine = lines[1];
    
    // Check for tab delimiters
    if (firstLine.includes('\t')) {
      const firstLineTabs = firstLine.split('\t').length;
      const secondLineTabs = secondLine.split('\t').length;
      
      // Consistent number of columns across lines suggests structured data
      if (firstLineTabs >= 2 && firstLineTabs === secondLineTabs) {
        return true;
      }
    }
    
    // Check for comma delimiters (CSV)
    if (firstLine.includes(',')) {
      const firstLineCommas = firstLine.split(',').length;
      const secondLineCommas = secondLine.split(',').length;
      
      // Consistent number of columns across lines suggests structured data
      if (firstLineCommas >= 2 && firstLineCommas === secondLineCommas) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Convert data to TSV format
   * @param {Array} data - Data array
   * @returns {string} - TSV string
   */
  convertToTSV(data) {
    if (!Array.isArray(data) || data.length === 0) {
      return '';
    }

    const headers = Object.keys(data[0]);
    const tsvLines = [headers.join('\t')];

    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header] || '';
        return typeof value === 'string' ? value : String(value);
      });
      tsvLines.push(values.join('\t'));
    });

    return tsvLines.join('\n');
  }

  /**
   * Normalize file item for consistent structure
   * @param {File} file - File object
   * @param {FileSystemFileHandle} handle - File handle
   * @param {string} relativePath - Relative path
   * @returns {Object} - Normalized file item
   */
  normalizeFileItem(file, handle, relativePath = null) {
    const name = file.name;
    const extension = name.includes('.') ? 
      name.substring(name.lastIndexOf('.')) : '';
    
    return {
      name,
      path: relativePath || name,
      type: 'file',
      size: file.size,
      lastModified: file.lastModified ? new Date(file.lastModified) : new Date(),
      extension,
      mimeType: file.type || undefined,
      handle, // Store the file handle for later use
      validationStatus: 'unvalidated',
      validationErrors: []
    };
  }

  /**
   * Check if we have permission to access the directory
   * @returns {Promise<boolean>} - Permission status
   */
  async checkPermission() {
    if (!this.directoryHandle) {
      return false;
    }

    try {
      // Query permission status
      const permission = await this.directoryHandle.queryPermission({ mode: 'readwrite' });
      return permission === 'granted';
    } catch (error) {
      return false;
    }
  }

  /**
   * Request permission for the directory
   * @returns {Promise<boolean>} - Permission granted status
   */
  async requestPermission() {
    if (!this.directoryHandle) {
      return false;
    }

    try {
      const permission = await this.directoryHandle.requestPermission({ mode: 'readwrite' });
      return permission === 'granted';
    } catch (error) {
      return false;
    }
  }

  /**
   * Create a new file in the project directory
   * @param {string} fileName - Name of the file to create
   * @param {any} content - Initial content
   * @returns {Promise<Object>} - Created file item
   */
  async createFile(fileName, content = '') {
    try {
      const directoryHandle = await this.getDirectoryHandle();
      
      // Create file handle
      const fileHandle = await directoryHandle.getFileHandle(fileName, { create: true });
      
      // Write initial content
      const writable = await fileHandle.createWritable();
      if (typeof content === 'object') {
        await writable.write(JSON.stringify(content, null, 2));
      } else {
        await writable.write(content.toString());
      }
      await writable.close();

      // Get file info and return normalized item
      const file = await fileHandle.getFile();
      const fileItem = this.normalizeFileItem(file, fileHandle);
      
      // Cache the handle
      this.fileHandles.set(fileName, fileHandle);
      
      return fileItem;
    } catch (error) {
      throw new Error(`Failed to create file ${fileName}: ${error.message}`);
    }
  }

  /**
   * Delete a file from the project directory
   * @param {Object} fileItem - File item to delete
   * @returns {Promise<boolean>} - Success status
   */
  async deleteFile(fileItem) {
    try {
      const directoryHandle = await this.getDirectoryHandle();
      const pathParts = fileItem.path.split('/');
      const fileName = pathParts[pathParts.length - 1];
      let parentDirectoryHandle = directoryHandle;

      for (let i = 0; i < pathParts.length - 1; i++) {
        parentDirectoryHandle = await parentDirectoryHandle.getDirectoryHandle(pathParts[i]);
      }

      await parentDirectoryHandle.removeEntry(fileName);
      
      // Remove from cache
      this.fileHandles.delete(fileItem.path);
      
      return true;
    } catch (error) {
      throw new Error(`Failed to delete file ${fileItem.name}: ${error.message}`);
    }
  }

  /**
   * Open package directory using File System Access API
   * @returns {Promise<FileSystemDirectoryHandle>} - Package directory handle
   */
  async openPackageDirectory() {
    if (!this.isSupported) {
      if (this.isFirefox) {
        throw new Error('File System Access API is not supported in Firefox. Please use Chrome, Edge, or Opera for full functionality. You can still use app in read-only mode.');
      } else {
        throw new Error('File System Access API is not supported in this browser. Please use a modern browser like Chrome, Edge, or Opera.');
      }
    }

    try {
      // Request directory handle from user
      this.packageDirectoryHandle = await window.showDirectoryPicker({
        mode: 'readwrite' // Request readwrite access for package operations
      });

      // Clear cached file handles for package directory
      this.packageFileHandles.clear();

      return this.packageDirectoryHandle;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Package directory selection was cancelled by the user.');
      } else if (error.name === 'NotFoundError') {
        throw new Error('The selected package directory was not found.');
      } else if (error.name === 'SecurityError') {
        throw new Error('Permission denied. Please ensure you have necessary permissions to access this directory.');
      } else {
        throw new Error(`Failed to open package directory: ${error.message}`);
      }
    }
  }

  /**
   * Get package directory handle (existing or request new one)
   * @returns {Promise<FileSystemDirectoryHandle>} - Package directory handle
   */
  async getPackageDirectoryHandle() {
    if (!this.packageDirectoryHandle) {
      return await this.openPackageDirectory();
    }
    
    // Verify we still have permission to access directory
    try {
      // Try to read the directory to verify permission
      // eslint-disable-next-line no-unused-vars
      for await (const _entry of this.packageDirectoryHandle.entries()) {
        break; // Just need to check if we can iterate
      }
      return this.packageDirectoryHandle;
    } catch (error) {
      if (error.name === 'SecurityError' || error.name === 'NotFoundError') {
        // Permission was revoked or directory was moved/deleted
        return await this.openPackageDirectory();
      }
      throw error;
    }
  }

  /**
   * Scan package directory for ZIP files
   * @returns {Promise<Array>} - Array of package info objects
   */
  async scanPackageDirectory() {
    const packageDirectoryHandle = await this.getPackageDirectoryHandle();
    const packages = [];

    try {
      for await (const [name, handle] of packageDirectoryHandle.entries()) {
        if (handle.kind === 'file' && name.endsWith('.zip')) {
          const file = await handle.getFile();
          const stats = await this.getPackageStats(file);
          const packageName = name.replace(/\.zip$/i, '');
          const version = stats.version || this.extractVersionFromName(name);
          const config = stats.config || {
            name: packageName,
            version,
            description: '',
            includeTextures: true,
            includeModels: true,
            includeHistory: true,
            compressionLevel: 6,
            selectedFiles: []
          };

          packages.push({
            id: name,
            name: packageName,
            version,
            created: file.lastModified ? new Date(file.lastModified) : new Date(),
            size: file.size,
            fileCount: stats.fileCount,
            config,
            source: 'local',
            syncStatus: 'local-only',
            checksum: stats.checksum
          });
        }
      }
    } catch (error) {
      console.error('Failed to scan package directory:', error);
    }

    return packages.sort((a, b) => b.created.getTime() - a.created.getTime());
  }

  /**
   * Get file handle from package directory path
   * @param {FileSystemDirectoryHandle} directoryHandle - Package directory handle
   * @param {string} relativePath - Relative path to file
   * @returns {Promise<FileSystemFileHandle>} - File handle
   */
  async getPackageFileHandleFromPath(directoryHandle, relativePath) {
    const pathParts = relativePath.split('/');
    let currentHandle = directoryHandle;

    // Navigate to parent directory
    for (let i = 0; i < pathParts.length - 1; i++) {
      currentHandle = await currentHandle.getDirectoryHandle(pathParts[i]);
    }

    // Get file handle
    const fileName = pathParts[pathParts.length - 1];
    return await currentHandle.getFileHandle(fileName);
  }

  /**
   * Extract version from package filename
   * @param {string} filename - Package filename
   * @returns {string} - Extracted version
   */
  extractVersionFromName(filename) {
    // Try to extract version from filename patterns
    const versionMatch = filename.match(/(\d{4}\.\d{2}\.\d{2})/);
    if (versionMatch) {
      return versionMatch[1];
    }
    
    // Fallback to date portion if available
    const dateMatch = filename.match(/(\d{4}-\d{2}-\d{2})/);
    if (dateMatch) {
      return dateMatch[1].replace(/-/g, '.');
    }
    
    return 'unknown';
  }

  /**
   * Get package statistics from ZIP file
   * @param {File} zipFile - ZIP file object
   * @returns {Promise<Object>} - Package statistics
   */
  async getPackageStats(zipFile) {
    try {
      const buffer = await zipFile.arrayBuffer();
      const zip = await JSZip.loadAsync(buffer);
      const entries = Object.values(zip.files);

      const fileEntries = entries.filter(entry => !entry.dir);
      const manifestEntry = zip.files['package-manifest.json'] || zip.files['package.json'];

      let manifest = null;
      if (manifestEntry) {
        const manifestText = await manifestEntry.async('string');
        try {
          manifest = JSON.parse(manifestText);
        } catch (parseError) {
          manifest = null;
        }
      }

      return {
        fileCount: fileEntries.length,
        hasManifest: !!manifestEntry,
        isComplete: !!manifest,
        version: manifest?.version,
        config: manifest?.config,
        checksum: manifest?.checksum || null
      };
    } catch (error) {
      return {
        fileCount: 0,
        hasManifest: false,
        isComplete: false,
        version: null,
        config: null,
        checksum: null
      };
    }
  }

  /**
   * Check whether package writable streams can be used for streaming ZIP output.
   * @returns {boolean}
   */
  supportsPackageWritableStreams() {
    return this.isSupported && !!this.packageDirectoryHandle;
  }

  /**
   * Create a writable stream for a package ZIP file.
   * @param {string} packageName - Name of the package
   * @returns {Promise<{ finalName: string, fileHandle: FileSystemFileHandle, writable: FileSystemWritableFileStream }>}
   */
  async createPackageWritableStream(packageName) {
    try {
      const packageDirectoryHandle = await this.getPackageDirectoryHandle();
      const finalName = packageName.endsWith('.zip') ? packageName : `${packageName}.zip`;
      const fileHandle = await packageDirectoryHandle.getFileHandle(finalName, { create: true });
      const writable = await fileHandle.createWritable();

      this.packageFileHandles.set(finalName, fileHandle);

      return {
        finalName,
        fileHandle,
        writable
      };
    } catch (error) {
      throw new Error(`Failed to create package writable stream: ${error.message}`);
    }
  }

  /**
   * Write package to package directory
   * @param {string} packageName - Name of the package
   * @param {Blob} zipContent - ZIP content as blob
   * @returns {Promise<boolean>} - Success status
   */
  async writePackageToDirectory(packageName, zipContent) {
    try {
      const packageDirectoryHandle = await this.getPackageDirectoryHandle();
      const finalName = packageName.endsWith('.zip') ? packageName : `${packageName}.zip`;
      const fileHandle = await packageDirectoryHandle.getFileHandle(finalName, { create: true });

      const writable = await fileHandle.createWritable();
      await writable.write(zipContent);
      await writable.close();

      this.packageFileHandles.set(finalName, fileHandle);

      return true;
    } catch (error) {
      throw new Error(`Failed to write package to directory: ${error.message}`);
    }
  }

  /**
   * Delete package from package directory
   * @param {string} packageName - Name of the package to delete
   * @returns {Promise<boolean>} - Success status
   */
  async deletePackageFromDirectory(packageName) {
    try {
      const packageDirectoryHandle = await this.getPackageDirectoryHandle();
      const finalName = packageName.endsWith('.zip') ? packageName : `${packageName}.zip`;
      await packageDirectoryHandle.removeEntry(finalName);

      this.packageFileHandles.delete(finalName);

      return true;
    } catch (error) {
      throw new Error(`Failed to delete package from directory: ${error.message}`);
    }
  }

  /**
   * Get package file handle from package directory
   * @param {string} packageName - Name of the package
   * @returns {Promise<FileSystemFileHandle>} - File handle
   */
  async getPackageFileHandle(packageName) {
    const packageDirectoryHandle = await this.getPackageDirectoryHandle();
    const finalName = packageName.endsWith('.zip') ? packageName : `${packageName}.zip`;

    if (this.packageFileHandles.has(finalName)) {
      return this.packageFileHandles.get(finalName);
    }

    const fileHandle = await packageDirectoryHandle.getFileHandle(finalName);
    this.packageFileHandles.set(finalName, fileHandle);
    return fileHandle;
  }

  /**
   * Read package ZIP content as Blob
   * @param {string} packageName - Name of the package
   * @returns {Promise<Blob>} - ZIP file blob
   */
  async readPackageBlob(packageName) {
    const packageHandle = await this.getPackageFileHandle(packageName);
    const file = await packageHandle.getFile();
    return file;
  }

  /**
   * Get currently selected package directory display name
   * @returns {string | null} - Directory name if selected
   */
  getPackageDirectoryName() {
    return this.packageDirectoryHandle?.name || null;
  }
}

export default FileSystemAccessService;
